/**
 * useLiveStreaming — the orchestration hook the `LiveStreamingPage` (Task 14)
 * consumes.
 *
 * Composes the Agora-media core (`useLiveStreamingAgora`, Task 9) and wires the
 * community-wall Firestore side-effects onto its lifecycle callbacks, porting the
 * Flutter `LiveStreamingScreenState` orchestration
 * (`lib/presentation/screens/community/live_streaming_screen.dart`):
 *   - broadcaster START on channel connected + END on dispose
 *     (`onJoinChannelSuccess`, `:421-445`; `dispose`, `:219-246`);
 *   - audience watcher count +1 on connected / −1 on dispose
 *     (`setLiveWatchers`, `:439`, `:249-251`);
 *   - the liveness subscription — watcher count + in-session end detection
 *     (`subscribeToBroadcastingMessageStats`, `:704-757`);
 *   - the Agora broadcaster-offline → ended path (`onUserOffline`, `:520-524`).
 *
 * This hook stays UI-FREE (architecture-review Must Fix, mirrored from
 * `useLiveStreamingAgora`): it NEVER calls `toast.*` and NEVER touches navigation
 * directly. The page-supplied `goBack` / `onJoinError` / `onTokenRenewError`
 * callbacks are forwarded straight through to the Agora core (the PAGE owns the
 * pop + the toasts). `useBasePage` also stays in the page (`presentation.md` —
 * page-only); this hook exposes the `onPageInit` / `onPageDispose` bridges the
 * page forwards into `useBasePage`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type {
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
} from 'agora-rtc-react';

import type { RootState } from '@/presentation/store';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { startCommunityLiveStreamUseCase } from '@/domain/usecases/liveStreaming/startCommunityLiveStreamUseCase';
import { endCommunityLiveStreamUseCase } from '@/domain/usecases/liveStreaming/endCommunityLiveStreamUseCase';
import { setCommunityLiveWatchersUseCase } from '@/domain/usecases/liveStreaming/setCommunityLiveWatchersUseCase';
import { subscribeToCommunityLiveStreamUseCase } from '@/domain/usecases/liveStreaming/subscribeToCommunityLiveStreamUseCase';
import type { LiveStreamStats } from '@/domain/services/liveStreaming/liveStreamingService';
import type { LatestLiveGiftInfoEntity } from '@/domain/entities/info/latestLiveGiftInfoEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { useLiveStreamingAgora } from '@/presentation/feature/liveStreaming/hooks/useLiveStreamingAgora';
import { clearWatchingLive } from '@/presentation/feature/liveStreaming/utils/liveAudiencePresence';
import {
  markBroadcastingLive,
  clearBroadcastingLive,
} from '@/presentation/feature/liveStreaming/utils/liveBroadcasterPresence';

export interface UseLiveStreamingArgs {
  /**
   * The broadcaster's Firebase uid — the Agora channel id (`:571`) AND the
   * identity `isBroadcaster` is derived against (`:149-151`). The wall owner /
   * message author on the start-live write.
   */
  broadcasterFirebaseId: string;
  /** The broadcaster's display name (message/notification author + live copy). */
  broadcasterName: string;
  /**
   * Audience-only: the broadcaster's resolved Agora streaming uid, read off the
   * live message so the video view matches the broadcaster's remote stream and
   * `onRemoteUserOffline` detects an in-session end (`:521`). Undefined on the
   * broadcaster side.
   */
  broadcasterStreamingId?: number;
  /** The live message `id` shared by the start/end writes + the liveness listener. */
  liveStreamingMessageId: string;
  /**
   * Page-supplied localized `"{name} is now Live"` copy (`:607`), interpolated
   * once by the page (l10n lives in presentation — the pure-domain start use case
   * takes the built string; this hook stays l10n-free). Fired verbatim on the
   * broadcaster start-live write.
   */
  liveText: string;
  /**
   * Page-supplied builder for the broadcaster end-live copy `"{name} was live for
   * {duration}"` (`:694`). On dispose the hook computes `liveDurationMs` and hands
   * it here; the page interpolates the l10n string with its own
   * `formatCallDuration` (l10n + date-formatting stay in presentation).
   * `broadcasterName` is already the page's arg, so it is not re-passed.
   */
  buildEndText: (liveDurationMs: number) => string;
  /**
   * Page-supplied pop, forwarded straight to the Agora core (navigation is the
   * page's concern). Should be page-memoized so the hook's dep arrays do not
   * churn.
   */
  goBack: () => void;
  /**
   * Page-supplied. Forwarded to the Agora core — fired when the session cannot be
   * entered (initial token mint failed). The PAGE fires the toast; this hook
   * stays UI-free.
   */
  onJoinError?: () => void;
  /**
   * Page-supplied. Forwarded to the Agora core — fired when a token could not be
   * regenerated mid-session. The PAGE fires the toast; this hook stays UI-free.
   */
  onTokenRenewError?: () => void;
}

export interface UseLiveStreamingResult {
  // --- Role + lifecycle state (the page's state-view gates) ---
  /** `_isBroadcaster` — `broadcasterFirebaseId === currentUser.firebaseId` (`:149-151`). */
  isBroadcaster: boolean;
  /** True until the channel is open (`_isOpeningChannel`, `:425`) — from the Agora core. */
  isOpeningChannel: boolean;
  /** `_isRenewingLiveStreamingToken` — the "regenerating token" overlay gate. */
  isRenewingToken: boolean;
  /**
   * `_liveStreamingEnded` — the broadcaster left the session (Agora `user-left`
   * for the broadcaster uid, `:520-524`, OR the audience-side `isLive:false`
   * liveness echo, `:723-727`). Drives the "streaming ended" state view.
   */
  streamingEnded: boolean;
  /** The live watcher count (`_watchersValueNotifier`, `:729-731`). Both roles display it. */
  watchers: number;
  /**
   * `_coinsValueNotifier` (`:735-737`) — the cumulative coins tipped in this
   * session. Updated only when the `coins` else-if branch is the one taken.
   * Rendered by the coins stat pill (`_coinsStats()`, `:1134-1176`; Task 5).
   */
  coins: number;
  /**
   * `_latestLiveGiftInfoValueNotifier` (`:111-112`, `:742`) — the transient
   * "last gift" the last-gift overlay shows, or `null` between gifts. Set on the
   * `coins` branch under `isLive && latestLiveGiftInfo != null` and nulled 5000 ms
   * later. Rendered by the last-gift overlay (`lastGift()`, `:922-1056`; Task 6).
   */
  latestLiveGift: LatestLiveGiftInfoEntity | null;
  /**
   * `_latestLiveGiftOpacity` (`:113`, `:741`/`:749`) — `1` while a gift is
   * showing, `0` once cleared. Mirrors the Flutter view-model field 1:1 (Task 6
   * renders it; as with `VideoCallLastGift` no visible CSS fade runs — the element
   * mounts/unmounts on show/clear).
   */
  latestLiveGiftOpacity: number;

  // --- Agora media handles (Task 11 video view) ---
  /** The broadcaster's local camera track (null for the audience). */
  localCameraTrack: ICameraVideoTrack | null;
  /** The remote users in the channel (the audience selects the broadcaster by uid). */
  remoteUsers: IAgoraRTCRemoteUser[];
  /**
   * Audience-only: the broadcaster's resolved streaming uid (passthrough of the
   * arg) — the remote uid the video view matches the broadcaster's stream on.
   */
  broadcasterStreamingId?: number;

  // --- Broadcaster control state + actions (Task 12 controls) ---
  /** `_isMute` (`:1508-1509`). */
  isMute: boolean;
  /** `_isCameraOff` (`:1552-1553`). */
  isCameraOff: boolean;
  /** `_clearScreen` — hides the controls/overlays on a video tap (`:1469`). */
  clearScreen: boolean;
  /** Toggle mic mute. */
  toggleMute: () => Promise<void>;
  /** Toggle camera. */
  toggleCamera: () => Promise<void>;
  /** Best-effort switch camera. */
  switchCamera: () => Promise<void>;
  /** Toggle `_clearScreen`. */
  toggleClearScreen: () => void;
  /** Number of available cameras (controls gate switch-camera on `< 2`). */
  cameraCount: number;

  // --- Lifecycle bridge (the page forwards into `useBasePage`) ---
  /**
   * `useBasePage.onInit` target — composes the Agora core's `onAgoraInit` (initial
   * token fetch) with the liveness subscription setup. Returns the combined
   * init-side teardown `useBasePage` runs on unmount.
   */
  onPageInit: () => () => void;
  /**
   * `useBasePage.onDispose` target — the Firestore end-side of `dispose` /
   * `disposeStreaming` (`:219-254`): broadcaster END (with the computed live
   * duration) / audience watcher −1, then cancel the subscription + the Agora
   * media teardown.
   */
  onPageDispose: () => void;
}

export const useLiveStreaming = (
  args: UseLiveStreamingArgs,
): UseLiveStreamingResult => {
  const {
    broadcasterFirebaseId,
    broadcasterName,
    broadcasterStreamingId,
    liveStreamingMessageId,
    liveText,
    buildEndText,
    goBack,
    onJoinError,
    onTokenRenewError,
  } = args;

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Role: `_isBroadcaster = broadcasterFirebaseId == currentFirebaseUser.uid`
  // (`:149-151`).
  const isBroadcaster = useMemo(
    () => broadcasterFirebaseId === currentUser?.firebaseId,
    [broadcasterFirebaseId, currentUser?.firebaseId],
  );

  // --- React state (the view-model the page renders) ---
  /** `_liveStreamingEnded` (`:528`). */
  const [streamingEnded, setStreamingEnded] = useState(false);
  /** `_watchersValueNotifier` (`:729-731`). */
  const [watchers, setWatchers] = useState(0);
  /** `_coinsValueNotifier` (`:109`, `:735-737`) — the coins-pill display value. */
  const [coins, setCoins] = useState(0);
  /** `_latestLiveGiftInfoValueNotifier` (`:111-112`, `:742`/`:750`). */
  const [latestLiveGift, setLatestLiveGift] =
    useState<LatestLiveGiftInfoEntity | null>(null);
  /** `_latestLiveGiftOpacity` (`:113`, `:741`/`:749`). */
  const [latestLiveGiftOpacity, setLatestLiveGiftOpacity] = useState(0);

  // --- Refs read from one-shot / cleanup contexts (always the latest value) ---
  /**
   * `_liveStartedTimestamp` (`:432`). A ref because `onPageDispose` must read the
   * LATEST value to compute `liveDuration` (`:235-236`) and it is written from the
   * connected handler that does not re-bind. Broadcaster only.
   */
  const liveStartedTimestampRef = useRef<number | null>(null);
  /** Guards the connected side-effect (start / watchers +1) so it fires once per role. */
  const connectedHandledRef = useRef(false);
  /**
   * Mirrors the Agora core's `isOpeningChannel` so `onPageDispose` reads the
   * LATEST value for the `!_isOpeningChannel` guards (`:231-232`, `:249`) rather
   * than a stale closure captured at mount (when it is still `true`).
   */
  const isOpeningChannelRef = useRef(true);
  /** The liveness-subscription `Unsubscribe` handle (`_streamSubscriptionBroadcastingStats`). */
  const unsubscribeRef = useRef<(() => void) | null>(null);
  /**
   * Idempotency guard for `onPageDispose`. The dispose runs from TWO surfaces —
   * `useBasePage.onDispose` (React unmount, SPA nav) AND `useBeforeUnload`
   * (tab close / reload / navigate-away, which unmount does NOT cover). On a
   * full-page reload both can fire, so this ref ensures the role's Firestore
   * end-side (watcher −1 / end-live write) runs at most once.
   */
  const hasDisposedRef = useRef(false);
  /**
   * Discriminator refs for Flutter's `watchers → likes → coins` else-if chain
   * (`:729-755`). The branch is decided synchronously per snapshot — before a
   * React state update has flushed — so the comparison must read a ref, not the
   * async state. The `0` initializer is an inert placeholder ONLY: the first
   * snapshot overwrites all three via the `hasBaselinedStatsRef` baseline below,
   * so the chain never diffs pre-existing server state against `0`. (Flutter
   * seeds its notifiers at `0` too — `:108-110` — and the parity companion
   * finding gives it the same baseline, so both clients stay identical.)
   * `likesRef` has no display state (no likes pill in scope) but is REQUIRED so a
   * likes-only snapshot does not fall through to the coins/gift branch.
   */
  const watchersRef = useRef(0);
  const likesRef = useRef(0);
  const coinsRef = useRef(0);
  /**
   * False until the liveness subscription has delivered its FIRST snapshot for
   * the current subscription. That first snapshot is a BASELINE, not a delta:
   * Firestore replays the whole doc as an `added` change, and both
   * `broadcastingInfo.coins` (a cumulative session total) and
   * `latestLiveGiftInfo` (written by `send_gift.ts` and NEVER cleared) can
   * already be non-empty from gifts sent BEFORE we joined — or before we
   * re-joined. Diffing those against refs seeded at `0` misreads pre-existing
   * server state as a fresh gift and replays a stale last-gift overlay on join
   * (user review round 1, observation 1). Seeding the refs from that first
   * snapshot instead means the overlay fires only for gifts that arrive AFTER
   * this join, which is the intent of the trigger.
   */
  const hasBaselinedStatsRef = useRef(false);
  /**
   * Holds the LAST-armed last-gift fade timer (`:744-753`) solely so
   * `onPageDispose` can cancel a still-pending one (Flutter's `if (mounted)`
   * guard, `:751`). NOT cleared when a new gift arms a fresh timer — see
   * `showLastGift`.
   */
  const latestLiveGiftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // ---------------------------------------------------------------------------
  // Agora core callbacks (passed INTO `useLiveStreamingAgora`). Defined before
  // the core call; they touch only refs / state / the Firestore use cases, never
  // the core's own result (which does not exist yet), so no ordering hazard.
  // ---------------------------------------------------------------------------

  /**
   * `onJoinChannelSuccess` side-effects (`:431-440`). Broadcaster → stamp the
   * live-started timestamp + fire the start-live write with the RESOLVED uid;
   * audience → bump the watcher count. Guarded to fire once per role.
   */
  const handleChannelConnected = useCallback(
    (localUid: number) => {
      if (connectedHandledRef.current) {
        return;
      }
      connectedHandledRef.current = true;

      if (isBroadcaster) {
        // `_liveStartedTimestamp = now` (`:432`).
        liveStartedTimestampRef.current = Date.now();
        // Fire-and-forget (Flutter `:433-437` does not await the write); log on
        // failure (video-call pattern).
        void (async () => {
          try {
            await startCommunityLiveStreamUseCase.execute({
              broadcasterFirebaseId,
              broadcasterName,
              broadcasterStreamingId: localUid,
              liveStreamingMessageId,
              liveText,
            });
          } catch (error) {
            appLogger.error(
              'startCommunityLiveStream failed',
              'useLiveStreaming.handleChannelConnected',
              { error: error as Error },
            );
          }
        })();
        // Note-2 broadcaster-presence flag (Task 12): record that this user is now
        // broadcasting a LIVE session in this tab, so the community composer's
        // go-live is disabled across every same-origin tab while it runs. Written
        // synchronously (not inside the fire-and-forget start-live write) so the
        // flag lands the instant the channel connects; cleared in `onPageDispose`
        // on EVERY exit path. Guarded on the current uid — the broadcaster IS the
        // current user here (`isBroadcaster`).
        if (currentUser?.firebaseId) {
          markBroadcastingLive(currentUser.firebaseId, liveStreamingMessageId);
        }
      } else {
        // `setLiveWatchers(true)` (`:439`).
        void (async () => {
          try {
            await setCommunityLiveWatchersUseCase.execute({
              liveStreamingMessageId,
              joined: true,
            });
          } catch (error) {
            appLogger.error(
              'setCommunityLiveWatchers(join) failed',
              'useLiveStreaming.handleChannelConnected',
              { error: error as Error },
            );
          }
        })();
      }
    },
    [
      isBroadcaster,
      broadcasterFirebaseId,
      broadcasterName,
      liveStreamingMessageId,
      liveText,
      currentUser?.firebaseId,
    ],
  );

  /**
   * `onUserOffline` → `onBroadcasterIsOffline` (`:520-524`): the audience's remote
   * broadcaster left — flag the session ended. The actual media teardown is left
   * to `onPageDispose` (the page shows the "ended" overlay first).
   */
  const handleRemoteUserOffline = useCallback(
    (uid: number) => {
      if (!isBroadcaster && uid === broadcasterStreamingId) {
        setStreamingEnded(true);
        // Session ended under the audience (broadcaster went offline) — release
        // the cross-tab presence flag (finding 2) so a stale flag does not block
        // a later legitimate re-join.
        if (currentUser?.firebaseId) {
          clearWatchingLive(currentUser.firebaseId, liveStreamingMessageId);
        }
      }
    },
    [
      isBroadcaster,
      broadcasterStreamingId,
      currentUser?.firebaseId,
      liveStreamingMessageId,
    ],
  );

  // --- Agora media core (Task 9) --------------------------------------------
  const {
    isOpeningChannel,
    isRenewingToken,
    localCameraTrack,
    remoteUsers,
    isMute,
    isCameraOff,
    clearScreen,
    toggleMute,
    toggleCamera,
    switchCamera,
    toggleClearScreen,
    cameraCount,
    onAgoraInit,
    onAgoraDispose,
  } = useLiveStreamingAgora({
    channel: broadcasterFirebaseId,
    isBroadcaster,
    goBack,
    onJoinError,
    onTokenRenewError,
    onChannelConnected: handleChannelConnected,
    onRemoteUserOffline: handleRemoteUserOffline,
  });

  // Mirror the core's opening-channel state so `onPageDispose` reads it fresh.
  useEffect(() => {
    isOpeningChannelRef.current = isOpeningChannel;
  }, [isOpeningChannel]);

  /**
   * Fire the transient "last gift" overlay (`:741-753`): set the gift + opacity
   * `1`, then arm a NEW independent per-gift fade timer WITHOUT cancelling any
   * earlier still-pending one — the faithful port of Flutter's uncancelled
   * per-gift `Future.delayed` (`:744-753`). With two gifts arriving in quick
   * succession the banner hides ~5 s after the FIRST gift (the second gift's
   * window is truncated to the first's `+5 s`), NOT granted a fresh full 5 s;
   * cancel-then-restart would diverge from Flutter (`workflow/lessons.md`
   * "Business logic deviations are bugs"). Mirrors `useVideoCall` (`:800-818`).
   * Because a later `setTimeout` may leave `latestLiveGiftTimeoutRef` pointing at
   * a newer timer than the one that actually fires, the fade callback nulls the
   * gift/opacity UNCONDITIONALLY; the ref holds only the last-armed timer so
   * `onPageDispose` can cancel the still-pending one.
   */
  const showLastGift = useCallback((gift: LatestLiveGiftInfoEntity) => {
    setLatestLiveGift(gift);
    setLatestLiveGiftOpacity(1);
    latestLiveGiftTimeoutRef.current = setTimeout(() => {
      setLatestLiveGiftOpacity(0);
      setLatestLiveGift(null);
    }, ANIMATION_CONSTANTS.duration.autoHideDelayLong);
  }, []);

  // ---------------------------------------------------------------------------
  // Liveness subscription (`subscribeToBroadcastingMessageStats`, `:704-757`).
  // Stable callback invoked once by `onPageInit`; stores the handle in
  // `unsubscribeRef` for both the init-side teardown and `onPageDispose`'s cancel.
  // ---------------------------------------------------------------------------
  const setUpSubscription = useCallback(() => {
    // A fresh subscription replays the full doc as an `added` change, so the
    // next snapshot is a baseline, not a delta. (Normally a no-op — the ref is
    // already false on a fresh mount — but it keeps the invariant true if the
    // subscription is ever re-armed within one mount.)
    hasBaselinedStatsRef.current = false;

    const unsubscribe = subscribeToCommunityLiveStreamUseCase.execute({
      liveStreamingMessageId,
      onStats: (stats: LiveStreamStats) => {
        // Skip `removed` (`:716-718`).
        if (stats.changeType === RemoteDocumentChangeType.removed) {
          return;
        }
        // Audience-only: `!isLive` → the broadcaster ended while / before we
        // joined (`:723-727`). Flag ended + tear the Agora media down, then skip
        // the watcher update for this change (Flutter `continue`s, `:726`). The
        // broadcaster's OWN `isLive:false` echo (its end write) is NOT triggered
        // here (the `!isBroadcaster` gate).
        if (!isBroadcaster && !stats.isLive) {
          setStreamingEnded(true);
          onAgoraDispose();
          // Session ended (broadcaster ended before / while we joined) — release
          // the cross-tab presence flag (finding 2) so a stale flag does not block
          // a later legitimate re-join.
          if (currentUser?.firebaseId) {
            clearWatchingLive(currentUser.firebaseId, liveStreamingMessageId);
          }
          return;
        }
        // FIRST snapshot = baseline, not a delta. Seed all three discriminators
        // from the doc as it stands at join time and mirror the two displayed
        // stats, then return WITHOUT firing the last-gift overlay: any
        // `latestLiveGiftInfo` on this snapshot predates our join (it is never
        // cleared server-side), and `coins` is the session total accrued before
        // we arrived — neither is a gift sent to us. Subsequent snapshots run
        // the unchanged else-if chain and so fire the overlay only for gifts
        // that land AFTER this join.
        //
        // Setting `watchers`/`coins` display state here (rather than only the
        // refs) also fixes the pills showing a stale `0` on join: the else-if
        // chain syncs at most ONE stat per snapshot, so without this the coins
        // pill sat at 0 until some later snapshot reached the coins branch.
        // Flutter gets the identical baseline (parity companion finding), so the
        // two clients stay in lockstep.
        if (!hasBaselinedStatsRef.current) {
          hasBaselinedStatsRef.current = true;
          watchersRef.current = stats.watchers;
          likesRef.current = stats.likes;
          coinsRef.current = stats.coins;
          setWatchers(stats.watchers);
          setCoins(stats.coins);
          return;
        }
        // Flutter's else-if chain (`:729-755`): on each snapshot AT MOST ONE of
        // watchers / likes / coins updates, checked in that priority order. The
        // discriminator order (watchers → likes → coins) is Flutter-load-bearing
        // (`workflow/lessons.md` — "preserve which condition is outermost"); the
        // refs (baselined from the first snapshot above, NOT `0`) — not React
        // state — decide the branch because it is chosen synchronously per
        // snapshot, before a state update has flushed. The `!=` guard is now LOAD-BEARING (unlike the old
        // unconditional `setWatchers`): it is what makes a likes-only or
        // coins-only snapshot fall through to the correct branch.
        if (stats.watchers !== watchersRef.current) {
          watchersRef.current = stats.watchers;
          setWatchers(stats.watchers);
        } else if (stats.likes !== likesRef.current) {
          // Ref-only (`:732-734`): likes is not displayed in scope, but this
          // branch MUST exist so a likes-only snapshot does NOT fall through to
          // the coins/gift branch.
          likesRef.current = stats.likes;
        } else if (stats.coins !== coinsRef.current) {
          coinsRef.current = stats.coins;
          setCoins(stats.coins);
          // Last-gift overlay fires ONLY on the coins branch under
          // `isLive && latestLiveGiftInfo != null` (`:739-754`).
          if (stats.isLive && stats.latestLiveGiftInfo != null) {
            showLastGift(stats.latestLiveGiftInfo);
          }
        }
      },
      onError: (error) => {
        appLogger.error(
          'subscribeToCommunityLiveStream error',
          'useLiveStreaming.subscription',
          { error },
        );
      },
    });
    unsubscribeRef.current = unsubscribe;
  }, [
    liveStreamingMessageId,
    isBroadcaster,
    onAgoraDispose,
    currentUser?.firebaseId,
    showLastGift,
  ]);

  // ---------------------------------------------------------------------------
  // Page-init bridge (`init`, `:201-217`). Composes the Agora core's init (initial
  // token fetch) with the liveness subscription setup. Returns the combined
  // init-side teardown `useBasePage` runs on unmount.
  // ---------------------------------------------------------------------------
  const onPageInit = useCallback((): (() => void) => {
    const agoraTeardown = onAgoraInit();
    setUpSubscription();

    // `onPageDispose` (run by `useBasePage` BEFORE this init-cleanup) nulls
    // `unsubscribeRef.current` after cancelling, so the `?.()` here is a safe
    // no-op on the normal path — no double-unsubscribe.
    return () => {
      agoraTeardown();
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      // Cancel a still-pending last-gift fade so it never fires after unmount
      // (Flutter's `if (mounted)` guard, `:751`; mirror `useVideoCall.ts:986-988`).
      // A no-op on the normal path — `onPageDispose` runs first and already
      // nulled the ref — but covers the init-only-then-unmount path.
      if (latestLiveGiftTimeoutRef.current) {
        clearTimeout(latestLiveGiftTimeoutRef.current);
        latestLiveGiftTimeoutRef.current = null;
      }
    };
  }, [onAgoraInit, setUpSubscription]);

  // ---------------------------------------------------------------------------
  // Page-dispose bridge (`dispose` / `disposeStreaming`, `:219-254`). Fires the
  // role's Firestore end-side, then cancels the subscription + tears the Agora
  // media down.
  // ---------------------------------------------------------------------------
  const onPageDispose = useCallback(() => {
    // Deduped against a double invocation (unmount + `beforeunload` on reload).
    if (hasDisposedRef.current) {
      return;
    }
    hasDisposedRef.current = true;

    if (isBroadcaster) {
      // Note-2 broadcaster-presence flag (Task 12): release this user's presence
      // for the session so a later go-live is allowed. Unconditional — NOT gated
      // on the end-live write's `!_isOpeningChannel && startedAt` guard below — so
      // the flag is cleared on EVERY broadcaster exit path. `onPageDispose` runs
      // from both `useBasePage.onDispose` (SPA-nav unmount) AND `useBeforeUnload`
      // (tab close / reload), and `hasDisposedRef` dedupes a double fire, so a
      // stale flag can never leave the community go-live permanently disabled
      // (Top-risk #3). `clearBroadcastingLive` no-ops when the flag was never set
      // (channel never connected → never marked), so the unconditional clear is
      // safe. Mirrors the audience-side `clearWatchingLive` in the `else` branch.
      if (currentUser?.firebaseId) {
        clearBroadcastingLive(currentUser.firebaseId, liveStreamingMessageId);
      }
      // `if (!_isOpeningChannel && _liveStartedTimestamp != null)` → end-live with
      // the computed duration (`:231-241`).
      const startedAt = liveStartedTimestampRef.current;
      if (!isOpeningChannelRef.current && startedAt != null) {
        const liveDurationMs = Date.now() - startedAt;
        // `"{name} was live for {duration}"` (`:694`) — the friendly duration is
        // l10n-driven (`formatCallDuration`), so the page builds it and hands the
        // finished string in (this hook stays l10n-free).
        const endText = buildEndText(liveDurationMs);
        // Unload-safe end write (finding_5): use the LIGHTWEIGHT `executeOnDispose`
        // path, which flips `isLive: false` via a single direct `setDoc` merge on
        // the directly-known `users/{owner}/community_messages/{messageId}` doc —
        // no transaction, no collection-group pre-query, no notification write — so
        // the write reliably flushes during `beforeunload` / tab-kill. The
        // transactional `execute` (kept for the in-session SPA-nav end) does a
        // server-side read-then-commit that the browser never lets land when it
        // tears the page down, leaving the message `isLive: true` forever.
        //
        // Fired fire-and-forget and NOT awaited before the `onAgoraDispose()`
        // below: the `setDoc` network request is initiated synchronously here, then
        // the Agora teardown runs, so the DB flush and the media teardown race in
        // PARALLEL (finding_5 step 3) — a slow `client.leave()` cannot delay the
        // flush, and the flush is not awaited before teardown.
        void (async () => {
          try {
            await endCommunityLiveStreamUseCase.executeOnDispose({
              broadcasterFirebaseId,
              liveStreamingMessageId,
              endText,
            });
          } catch (error) {
            appLogger.error(
              'endCommunityLiveStream(onDispose) failed',
              'useLiveStreaming.onPageDispose',
              { error: error as Error },
            );
          }
        })();
      }
    } else {
      // Release the cross-tab presence flag (finding 2) so a later re-join is
      // allowed. Unconditional (not gated on the channel having opened) because
      // the flag is set at the "Join Live" tap BEFORE the channel opens, so it
      // must be cleared even if the channel never opened — otherwise a stale flag
      // would block re-join. With finding 3's `beforeunload`/`pagehide` bridge,
      // this also runs on a tab close / kill, releasing the flag on that path too.
      if (currentUser?.firebaseId) {
        clearWatchingLive(currentUser.firebaseId, liveStreamingMessageId);
      }
      // Audience: `if (!_isBroadcaster && !_isOpeningChannel) setLiveWatchers(false)`
      // (`:249-251`) — only decrement if the audience actually joined (channel
      // opened).
      if (!isOpeningChannelRef.current) {
        void (async () => {
          try {
            await setCommunityLiveWatchersUseCase.execute({
              liveStreamingMessageId,
              joined: false,
            });
          } catch (error) {
            appLogger.error(
              'setCommunityLiveWatchers(leave) failed',
              'useLiveStreaming.onPageDispose',
              { error: error as Error },
            );
          }
        })();
      }
    }

    // Cancel the liveness subscription (`:228-229`).
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    // Cancel a still-pending last-gift fade so it never fires after unmount —
    // the web equivalent of Flutter guarding its delayed `setState` with
    // `if (mounted)` (`:751`; mirror `useVideoCall.ts:986-988`). This is the
    // ONLY `clearTimeout` on the gift timer — never on a new gift (`showLastGift`
    // arms fresh timers without cancelling).
    if (latestLiveGiftTimeoutRef.current) {
      clearTimeout(latestLiveGiftTimeoutRef.current);
      latestLiveGiftTimeoutRef.current = null;
    }

    // Media teardown (`disposeStreaming` leaveChannel/release, `:252-253`).
    onAgoraDispose();
  }, [
    isBroadcaster,
    broadcasterFirebaseId,
    liveStreamingMessageId,
    buildEndText,
    onAgoraDispose,
    currentUser?.firebaseId,
  ]);

  return {
    isBroadcaster,
    isOpeningChannel,
    isRenewingToken,
    streamingEnded,
    watchers,
    coins,
    latestLiveGift,
    latestLiveGiftOpacity,

    localCameraTrack,
    remoteUsers,
    broadcasterStreamingId,

    isMute,
    isCameraOff,
    clearScreen,
    toggleMute,
    toggleCamera,
    switchCamera,
    toggleClearScreen,
    cameraCount,

    onPageInit,
    onPageDispose,
  };
};
